<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content package-modal" @click.stop>
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <h3>
            <i class="fas fa-box-open"></i>
            {{ mode === 'create' ? 'Nouveau Package' : 'Modifier le Package' }}
          </h3>
          <p class="modal-subtitle">{{ mode === 'create' ? 'Créez un nouveau package de produits' : 'Modifiez les informations du package' }}</p>
        </div>
        <button @click="closeModal" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="modal-body">
        <form @submit.prevent="handleSubmit">
          <!-- Informations générales -->
          <div class="section">
            <div class="section-header">
              <i class="fas fa-info-circle"></i>
              <h4>Informations Générales</h4>
            </div>
            <div class="section-content">
              <div class="form-row">
                <div class="form-group">
                  <label>Nom du Package *</label>
                  <input
                    v-model="form.nom"
                    type="text"
                    class="form-control"
                    placeholder="Ex: Pack Installation GPS"
                    required
                  />
                </div>
                <div class="form-group">
                  <label>Référence *</label>
                  <input
                    v-model="form.reference"
                    type="text"
                    class="form-control"
                    placeholder="Ex: PKG-001"
                    required
                  />
                </div>
              </div>

              <div class="form-group">
                <label>Description</label>
                <textarea
                  v-model="form.description"
                  class="form-control"
                  rows="3"
                  placeholder="Description du package..."
                ></textarea>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Prix Unitaire (TND) *</label>
                  <input
                    v-model.number="form.prixUnitaire"
                    type="number"
                    step="0.01"
                    min="0"
                    class="form-control"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div class="form-group">
                  <label>Quantité en Stock *</label>
                  <input
                    v-model.number="form.quantiteStock"
                    type="number"
                    min="0"
                    class="form-control"
                    placeholder="0"
                    required
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="checkbox-label">
                  <input
                    v-model="form.actif"
                    type="checkbox"
                    class="checkbox-input"
                  />
                  <span class="checkbox-text">Package actif</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Sélection des produits -->
          <div class="section">
            <div class="section-header">
              <i class="fas fa-cubes"></i>
              <h4>Produits du Package</h4>
            </div>
            <div class="section-content">
              <!-- Recherche de produits -->
              <div class="product-search">
                <div class="search-box">
                  <i class="fas fa-search"></i>
                  <input
                    v-model="productSearch"
                    type="text"
                    class="search-input"
                    placeholder="Rechercher un produit à ajouter..."
                  />
                </div>
              </div>

              <!-- Liste des produits disponibles -->
              <div class="available-products" v-if="filteredAvailableProducts.length > 0">
                <h5 class="subsection-title">
                  <i class="fas fa-warehouse"></i>
                  Produits Disponibles
                </h5>
                <div class="products-grid">
                  <div
                    v-for="product in filteredAvailableProducts"
                    :key="product._id"
                    class="product-card"
                    @click="addProduct(product)"
                  >
                    <div class="product-icon">
                      <i class="fas fa-cube"></i>
                    </div>
                    <div class="product-details">
                      <div class="product-name">{{ product.designation || product.nom }}</div>
                      <div class="product-ref">Réf: {{ product.referenceInterne || product.reference }}</div>
                      <div class="product-category" v-if="product.categorie">
                        <i class="fas fa-tag"></i>
                        {{ product.categorie }}
                      </div>
                      <div class="product-status">
                        <i class="fas fa-circle"></i>
                        <span :class="getStatusClass(product.statut)">
                          {{ formatStatus(product.statut) }}
                        </span>
                      </div>
                    </div>
                    <button type="button" class="btn-add-product">
                      <i class="fas fa-plus"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Produits sélectionnés -->
              <div class="selected-products" v-if="form.produits.length > 0">
                <h5 class="subsection-title">
                  <i class="fas fa-check-circle"></i>
                  Produits Sélectionnés ({{ form.produits.length }})
                </h5>
                <div class="selected-products-list">
                  <div
                    v-for="(item, index) in form.produits"
                    :key="item.produit._id || item.produit"
                    class="selected-product-item"
                  >
                    <div class="product-info">
                      <i class="fas fa-cube"></i>
                      <span class="product-name">{{ getProductName(item.produit) }}</span>
                    </div>
                    <div class="quantity-control">
                      <button
                        type="button"
                        @click="decreaseQuantity(index)"
                        class="btn-quantity"
                      >
                        <i class="fas fa-minus"></i>
                      </button>
                      <input
                        v-model.number="item.quantite"
                        type="number"
                        min="1"
                        class="quantity-input"
                      />
                      <button
                        type="button"
                        @click="increaseQuantity(index)"
                        class="btn-quantity"
                      >
                        <i class="fas fa-plus"></i>
                      </button>
                    </div>
                    <button
                      type="button"
                      @click="removeProduct(index)"
                      class="btn-remove"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="no-products-selected">
                <i class="fas fa-inbox"></i>
                <p>Aucun produit sélectionné</p>
                <p class="hint">Cliquez sur un produit ci-dessus pour l'ajouter au package</p>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="modal-actions">
            <button type="button" @click="closeModal" class="btn-cancel">
              <i class="fas fa-times"></i>
              Annuler
            </button>
            <button type="submit" class="btn-submit" :disabled="submitting">
              <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
              {{ submitting ? 'Enregistrement...' : 'Enregistrer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'

export default {
  name: 'PackageModal',
  props: {
    package: {
      type: Object,
      default: null
    },
    mode: {
      type: String,
      default: 'create',
      validator: (value) => ['create', 'edit'].includes(value)
    },
    availableProducts: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const submitting = ref(false)
    const productSearch = ref('')

    const form = reactive({
      nom: '',
      reference: '',
      description: '',
      prixUnitaire: 0,
      quantiteStock: 0,
      actif: true,
      produits: []
    })

    // Initialiser le formulaire si en mode édition
    if (props.mode === 'edit' && props.package) {
      Object.assign(form, {
        nom: props.package.nom,
        reference: props.package.reference,
        description: props.package.description || '',
        prixUnitaire: props.package.prixUnitaire,
        quantiteStock: props.package.quantiteStock,
        actif: props.package.actif !== false,
        produits: props.package.produits?.map(p => ({
          produit: p.produit._id || p.produit,
          quantite: p.quantite
        })) || []
      })
    }

    const filteredAvailableProducts = computed(() => {
      let products = props.availableProducts.filter(p => {
        // Exclure les produits déjà sélectionnés
        return !form.produits.some(item => {
          const produitId = item.produit._id || item.produit
          return produitId === p._id
        })
      })

      // Filtrer par recherche
      if (productSearch.value) {
        const query = productSearch.value.toLowerCase()
        products = products.filter(p =>
          p.designation?.toLowerCase().includes(query) ||
          p.nom?.toLowerCase().includes(query) ||
          p.referenceInterne?.toLowerCase().includes(query) ||
          p.reference?.toLowerCase().includes(query) ||
          p.categorie?.toLowerCase().includes(query)
        )
      }

      return products
    })

    const addProduct = (product) => {
      form.produits.push({
        produit: product._id,
        quantite: 1,
        _productData: product // Pour l'affichage
      })
    }

    const removeProduct = (index) => {
      form.produits.splice(index, 1)
    }

    const increaseQuantity = (index) => {
      form.produits[index].quantite++
    }

    const decreaseQuantity = (index) => {
      if (form.produits[index].quantite > 1) {
        form.produits[index].quantite--
      }
    }

    const getProductName = (produit) => {
      if (typeof produit === 'object' && (produit.designation || produit.nom)) {
        return produit.designation || produit.nom
      }
      // Chercher dans les produits disponibles ou dans _productData
      const found = props.availableProducts.find(p => p._id === produit)
      return found?.designation || found?.nom || 'Produit inconnu'
    }

    const getStockClass = (stock) => {
      if (stock === 0) return 'stock-zero'
      if (stock < 10) return 'stock-low'
      return 'stock-ok'
    }

    const getStatusClass = (statut) => {
      const statusMap = {
        'en_stock': 'status-available',
        'affecte_technicien': 'status-assigned',
        'en_cours': 'status-assigned',
        'installe': 'status-installed',
        'retourne': 'status-returned',
        'defectueux': 'status-defective',
        'perdu': 'status-lost'
      }
      return statusMap[statut] || 'status-default'
    }

    const formatStatus = (statut) => {
      const statusLabels = {
        'en_stock': 'En stock',
        'affecte_technicien': 'Affecté',
        'en_cours': 'En cours',
        'installe': 'Installé',
        'retourne': 'Retourné',
        'defectueux': 'Défectueux',
        'perdu': 'Perdu'
      }
      return statusLabels[statut] || statut
    }

    const handleSubmit = async () => {
      submitting.value = true
      try {
        const packageData = {
          ...form,
          produits: form.produits.map(p => ({
            produit: p.produit._id || p.produit,
            quantite: p.quantite
          }))
        }
        emit('save', packageData)
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        submitting.value = false
      }
    }

    const closeModal = () => {
      emit('close')
    }

    return {
      form,
      submitting,
      productSearch,
      filteredAvailableProducts,
      addProduct,
      removeProduct,
      increaseQuantity,
      decreaseQuantity,
      getProductName,
      getStockClass,
      getStatusClass,
      formatStatus,
      handleSubmit,
      closeModal
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
}

.modal-content {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.modal-header {
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-subtitle {
  margin: 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
}

.section {
  margin-bottom: 2rem;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e5e7eb;
}

.section-header i {
  color: var(--primary-blue);
  font-size: 1.25rem;
}

.section-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.section-content {
  padding-left: 2.25rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

textarea.form-control {
  resize: vertical;
  font-family: inherit;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.checkbox-label:hover {
  background: #f3f4f6;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.checkbox-text {
  font-weight: 500;
  color: #374151;
}

/* Recherche de produits */
.product-search {
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 1rem;
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(0, 27, 183, 0.1);
}

/* Produits disponibles */
.available-products,
.selected-products {
  margin-bottom: 2rem;
}

.subsection-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.subsection-title i {
  color: var(--primary-blue);
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  max-height: 300px;
  overflow-y: auto;
  padding: 0.5rem;
}

.product-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.product-card:hover {
  background: white;
  border-color: var(--primary-blue);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.15);
}

.product-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border-radius: 10px;
  font-size: 1.25rem;
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.product-ref {
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.product-category {
  font-size: 0.7rem;
  color: var(--primary-blue);
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
}

.product-category i {
  font-size: 0.65rem;
}

.product-status {
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
}

.product-status i {
  font-size: 0.5rem;
}

.status-available {
  color: #059669;
}

.status-available i {
  color: #059669;
}

.status-assigned {
  color: #f59e0b;
}

.status-assigned i {
  color: #f59e0b;
}

.status-installed {
  color: #3b82f6;
}

.status-installed i {
  color: #3b82f6;
}

.status-returned {
  color: #8b5cf6;
}

.status-returned i {
  color: #8b5cf6;
}

.status-defective {
  color: #ef4444;
}

.status-defective i {
  color: #ef4444;
}

.status-lost {
  color: #6b7280;
}

.status-lost i {
  color: #6b7280;
}

.status-default {
  color: #9ca3af;
}

.stock-ok {
  color: #059669;
  font-weight: 600;
}

.stock-low {
  color: #d97706;
  font-weight: 600;
}

.stock-zero {
  color: #dc2626;
  font-weight: 600;
}

.btn-add-product {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-add-product:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

/* Produits sélectionnés */
.selected-products-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.selected-product-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
}

.product-info {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.product-info i {
  color: var(--primary-blue);
  font-size: 1.25rem;
}

.product-info .product-name {
  font-weight: 600;
  color: #1f2937;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-quantity {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-quantity:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.3);
}

.quantity-input {
  width: 60px;
  padding: 0.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  font-size: 0.95rem;
}

.quantity-input:focus {
  outline: none;
  border-color: var(--primary-blue);
}

.btn-remove {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-remove:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.no-products-selected {
  text-align: center;
  padding: 3rem 2rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px dashed #d1d5db;
}

.no-products-selected i {
  font-size: 3rem;
  color: #d1d5db;
  margin-bottom: 1rem;
}

.no-products-selected p {
  margin: 0.5rem 0;
  color: #6b7280;
}

.no-products-selected .hint {
  font-size: 0.85rem;
  color: #9ca3af;
}

/* Actions */
.modal-actions {
  display: flex;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: #f9fafb;
  border-top: 2px solid #e5e7eb;
}

.btn-cancel,
.btn-submit {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-cancel {
  background: white;
  color: #6b7280;
  border: 2px solid #e5e7eb;
}

.btn-cancel:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-submit {
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  color: white;
  box-shadow: 0 4px 12px rgba(0, 27, 183, 0.2);
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 27, 183, 0.3);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .modal-actions {
    flex-direction: column;
  }
}
</style>
